/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * useOnlineUsers Hook - Real-time online users tracking via WebSocket
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../services/api'
import webSocketService from '../services/webSocketService'

import { createLogger } from '../utils/logger'

const logger = createLogger('useOnlineUsers')

/**
 * Validate presence update data structure
 * @param {any} data - Data to validate
 * @returns {boolean} - True if valid
 */
function isValidPresenceData(data) {
  if (!data || typeof data !== 'object') return false
  if (typeof data.onlineCount !== 'number') return false
  if (!['connected', 'disconnected'].includes(data.eventType)) return false
  return true
}

/**
 * Hook to track online users in real-time via WebSocket
 * Uses optimistic updates from presence events to avoid race conditions
 * @returns {Object} - { onlineCount, users, isLoading, error, isConnected, refetch }
 */
export function useOnlineUsers() {
  const [onlineCount, setOnlineCount] = useState(0)
  const [users, setUsers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isConnected, setIsConnected] = useState(webSocketService.isConnected())

  // Refs to prevent memory leaks and ensure stable callbacks
  const mountedRef = useRef(true)
  const fetchInProgressRef = useRef(false)

  // Fetch online users data from API
  // This callback has an empty dependency array intentionally - it only uses refs and setState
  // functions which are stable across renders. This is safe in the useEffect dependency array
  // because the callback identity never changes, preventing unnecessary effect re-runs.
  const fetchOnlineUsers = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchInProgressRef.current) return

    try {
      fetchInProgressRef.current = true
      setIsLoading(true)
      setError(null)
      const response = await api.get('/admin/online-users')

      if (mountedRef.current) {
        setOnlineCount(response.data.onlineCount)
        setUsers(response.data.users || [])
      }
    } catch (err) {
      if (mountedRef.current) {
        logger.error('Failed to fetch online users:', err)
        setError(err.message || 'Failed to fetch online users')
      }
    } finally {
      fetchInProgressRef.current = false
      if (mountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true

    // Initial fetch
    fetchOnlineUsers()

    // Event handlers
    const handlePresenceUpdate = (data) => {
      try {
        if (!mountedRef.current) return

        // Validate data structure to prevent crashes from malformed events
        if (!isValidPresenceData(data)) {
          logger.warn('Invalid presence update data received:', data)
          return
        }

        logger.debug('📡 Presence update received:', data)

        // Update the online count immediately from the event
        setOnlineCount(data.onlineCount)

        // Optimistically update the users array based on the event
        // This avoids race conditions from debounced refetches
        // Note: Full user details (email, isAdmin) will be available on next API fetch
        // or WebSocket reconnection. This is intentional to keep presence events lightweight.
        if (data.user && data.user.id) {
          if (data.eventType === 'connected') {
            // Add user if not already in list
            setUsers((prevUsers) => {
              const exists = prevUsers.some((u) => u.id === data.user.id)
              if (exists) return prevUsers
              // Add minimal user info - full details will come on next full fetch
              return [...prevUsers, { id: data.user.id, name: data.user.name, deviceCount: 1 }]
            })
          } else if (data.eventType === 'disconnected') {
            // Remove user from list
            setUsers((prevUsers) => prevUsers.filter((u) => u.id !== data.user.id))
          }
        }
      } catch (err) {
        logger.error('Error handling presence update:', err)
        if (mountedRef.current) {
          setError('Failed to process presence update')
        }
      }
    }

    const handleConnect = () => {
      if (mountedRef.current) {
        setIsConnected(true)
        // Refetch when WebSocket reconnects to ensure data is fresh
        fetchOnlineUsers()
      }
    }

    const handleDisconnect = () => {
      if (mountedRef.current) {
        setIsConnected(false)
      }
    }

    // Subscribe to WebSocket events
    webSocketService.on('presence:update', handlePresenceUpdate)
    webSocketService.onConnect(handleConnect)
    webSocketService.onDisconnect(handleDisconnect)

    // Update connection status
    setIsConnected(webSocketService.isConnected())

    // Cleanup
    return () => {
      mountedRef.current = false
      webSocketService.off('presence:update', handlePresenceUpdate)
      webSocketService.offConnect(handleConnect)
      webSocketService.offDisconnect(handleDisconnect)
    }
  }, [fetchOnlineUsers])

  return {
    onlineCount,
    users,
    isLoading,
    error,
    isConnected,
    refetch: fetchOnlineUsers
  }
}

export default useOnlineUsers
